import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "filterContactType", "filterCountry", "filterCity", "searchInput", "clearFilters", "activeFilters", "sidebarContent", "sidebarToggleIcon"]
  static values = {
    contacts: Array,
    centerLat: Number,
    centerLng: Number,
    zoom: Number
  }

  connect() {
    
    if (typeof mapboxgl === 'undefined') {
      console.error("Mapbox GL JS is not loaded")
      return
    }

    // Check if we have the map target
    if (!this.hasMapTarget) {
      console.error("Map target not found")
      return
    }

    // Initialize state
    this.currentFilters = {
      contactType: 'all',
      country: 'all',
      city: 'all',
      radius: null
    }

    this.hoveredMarkerId = null
    this.hoveredRowId = null
    this.sidebarExpanded = true

    // Initialize map
    this.initializeMap()
  }

  disconnect() {
    // Clean up map
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    const token = this.authToken
    if (!token) {
      console.error("Mapbox token not found")
      this.showMapError("Token Mapbox manquant")
      return
    }

    mapboxgl.accessToken = token

    try {
      // Wait for the map container to be properly sized
      setTimeout(() => {
        this.map = new mapboxgl.Map({
          container: this.mapTarget,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [this.centerLngValue || 2.3522, this.centerLatValue || 48.8566],
          zoom: this.zoomValue || 5,
          projection: 'mercator', // Use flat Mercator projection instead of globe
          pitch: 0, // Disable tilt
          bearing: 0 // Reset rotation
        })

        this.map.addControl(new mapboxgl.NavigationControl())

        this.map.on('load', () => {
          this.addContactMarkersWithClustering()
          this.setupMapInteractions()
        })

        this.map.on('error', (e) => {
          console.error("Map error:", e)
          this.showMapError("Erreur lors du chargement de la carte")
        })

        // Handle map resize when sidebar toggles
        this.map.on('idle', () => {
          this.map.resize()
        })
      }, 100)
    } catch (error) {
      console.error("Failed to initialize map:", error)
      this.showMapError("Impossible d'initialiser la carte")
    }
  }

  setupTableInteractions() {
    // Setup interactions for sidebar contact items
    const setupSidebarInteractions = () => {
      const contactItems = document.querySelectorAll('.contact-item[data-contact-id]')

      contactItems.forEach(item => {
        const contactId = item.dataset.contactId

        // Remove existing event listeners to avoid duplicates
        item.replaceWith(item.cloneNode(true))
      })

      // Re-query after replacing to get the new elements
      document.querySelectorAll('.contact-item[data-contact-id]').forEach(item => {
        const contactId = item.dataset.contactId

        // Hover highlight
        item.addEventListener('mouseenter', () => {
          item.classList.add('bg-gray-100')
          this.highlightMapMarker(contactId)
        })

        item.addEventListener('mouseleave', () => {
          item.classList.remove('bg-gray-100')
          this.unhighlightMapMarker(contactId)
        })

        // Click to center
        item.addEventListener('click', (e) => {
          // Don't trigger if clicking on dropdown or links
          if (e.target.closest('.dropdown-in-list') || e.target.closest('a')) {
            return
          }
          this.centerMapOnContact(contactId)
        })
      })
    }

    // Setup immediately and also after any Turbo frame loads
    setupSidebarInteractions()
    document.addEventListener('turbo:frame-load', setupSidebarInteractions)
  }

  highlightMapMarker(contactId) {
    if (!this.map || !this.map.getSource('contacts')) return

    // Find the contact data
    const contact = this.contactsValue.find(c => c.id == contactId)
    if (!contact || !contact.latitude || !contact.longitude) {
      return
    }

    // Remove any previous highlight
    if (this.hoveredMarkerId && this.hoveredMarkerId !== contactId) {
      this.map.setFeatureState(
        { source: 'contacts', id: parseInt(this.hoveredMarkerId) },
        { hover: false }
      )
    }

    // Set feature state for highlight
    this.map.setFeatureState(
      { source: 'contacts', id: parseInt(contactId) },
      { hover: true }
    )

    // Store current hovered marker
    this.hoveredMarkerId = contactId
  }

  unhighlightMapMarker(contactId) {
    if (!this.map || !this.map.getSource('contacts')) return

    // Remove feature state
    this.map.setFeatureState(
      { source: 'contacts', id: parseInt(contactId) },
      { hover: false }
    )

    // Clear if this was the hovered marker
    if (this.hoveredMarkerId == contactId) {
      this.hoveredMarkerId = null
    }
  }

  centerMapOnContact(contactId) {
    const contact = this.contactsValue.find(c => c.id == contactId)
    if (!contact || !contact.latitude || !contact.longitude) return

    this.map.flyTo({
      center: [contact.longitude, contact.latitude],
      zoom: 14,
      duration: 1000
    })
  }

  setupMapInteractions() {
    // Hover effect on clusters
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })

    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = ''
    })

    // Hover effect on individual points
    this.map.on('mouseenter', 'unclustered-point', (e) => {
      this.map.getCanvas().style.cursor = 'pointer'

      const properties = e.features[0].properties
      const contactId = properties.id

      // Highlight the corresponding table row
      this.highlightTableRow(contactId)
    })

    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = ''

      // Remove highlight from table row
      this.unhighlightTableRow()
    })

    // Click on marker
    this.map.on('click', 'unclustered-point', (e) => {
      const properties = e.features[0].properties

      // Parse properties (they come as strings from GeoJSON)
      const contact = {
        id: properties.id,
        name: properties.name,
        organization: properties.organization,
        city: properties.city,
        country: properties.country,
        email: properties.email,
        contact_type_abbr: properties.contact_type_abbr,
        show_url: properties.show_url,
        edit_url: properties.edit_url,
        latitude: e.features[0].geometry.coordinates[1],
        longitude: e.features[0].geometry.coordinates[0]
      }

      this.showContactPopup(contact)
    })

    // Click on cluster
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })

      const clusterId = features[0].properties.cluster_id
      this.map.getSource('contacts').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        }
      )
    })
  }

  highlightTableRow(contactId) {
    // Now highlights sidebar items instead of table rows
    this.unhighlightTableRow()

    // Find and highlight the sidebar item
    const item = document.getElementById(`sidebar_contact_${contactId}`)
    if (item) {
      // Apply highlight with classes
      item.classList.add('bg-yellow-50', 'ring-2', 'ring-primary')

      // Scroll into view in sidebar - center the item
      item.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })

      // Store the highlighted item ID
      this.hoveredItemId = `sidebar_contact_${contactId}`
    }
  }

  unhighlightTableRow() {
    // Now unhighlights sidebar items
    if (this.hoveredItemId) {
      const item = document.getElementById(this.hoveredItemId)
      if (item) {
        item.classList.remove('bg-yellow-50', 'ring-2', 'ring-primary')
      }
      this.hoveredItemId = null
    }
  }

  addContactMarkersWithClustering() {
    // Filter contacts with coordinates
    const contactsWithCoords = this.contactsValue.filter(c => c.latitude && c.longitude)

    // Apply jitter to prevent stacked markers at same location
    const contactsWithJitter = this.applyJitterToOverlappingContacts(contactsWithCoords)

    // Convert to GeoJSON - IMPORTANT: add id at the root level for setFeatureState to work
    const geojson = {
      type: 'FeatureCollection',
      features: contactsWithJitter.map((contact, index) => ({
        type: 'Feature',
        id: parseInt(contact.id), // IMPORTANT: id must be at the root level of the feature
        geometry: {
          type: 'Point',
          coordinates: [contact.longitude, contact.latitude]
        },
        properties: {
          id: contact.id,
          name: contact.name,
          organization: contact.organization,
          city: contact.city,
          country: contact.country,
          email: contact.email,
          contact_type_abbr: contact.contact_type_abbr,
          show_url: contact.show_url,
          edit_url: contact.edit_url
        }
      }))
    }

    // Add source
    this.map.addSource('contacts', {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Add cluster layer
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'contacts',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6',
          15,
          '#f1f075',
          30,
          '#f28cb1'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          15,
          30,
          30,
          40
        ]
      }
    })

    // Add cluster count layer
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'contacts',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      }
    })

    // Add individual contact points
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'contacts',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#f59e0b',  // Orange when hovered
          '#10b981'   // Green for contacts (different from venues which are blue)
        ],
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          10,  // Larger when hovered
          7    // Default size
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    })

    // Fit map to contact bounds
    if (contactsWithCoords.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      contactsWithCoords.forEach(contact => {
        bounds.extend([contact.longitude, contact.latitude])
      })
      this.map.fitBounds(bounds, { padding: 50 })
    }
  }

  // Filter methods
  filterContacts() {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    const contactType = this.hasFilterContactTypeTarget ? this.filterContactTypeTarget.value : 'all'
    const country = this.hasFilterCountryTarget ? this.filterCountryTarget.value : 'all'
    const city = this.hasFilterCityTarget ? this.filterCityTarget.value : 'all'

    // Filter contacts
    const filteredContacts = this.contactsValue.filter(contact => {
      // Search filter
      if (searchTerm &&
          !contact.name?.toLowerCase().includes(searchTerm) &&
          !contact.organization?.toLowerCase().includes(searchTerm) &&
          !contact.city?.toLowerCase().includes(searchTerm) &&
          !contact.country?.toLowerCase().includes(searchTerm) &&
          !contact.email?.toLowerCase().includes(searchTerm)) {
        return false
      }

      // Contact type filter
      if (contactType !== 'all' && contact.contact_type_abbr !== contactType) {
        return false
      }

      // Country filter
      if (country !== 'all' && contact.country !== country) {
        return false
      }

      // City filter
      if (city !== 'all' && contact.city !== city) {
        return false
      }

      return true
    })

    // Update map markers
    this.updateMapMarkers(filteredContacts)

    // Update sidebar
    this.updateSidebar(filteredContacts)

    // Show/hide clear button
    if (searchTerm || contactType !== 'all' || country !== 'all' || city !== 'all') {
      this.clearFiltersTarget?.classList.remove('hidden')
    } else {
      this.clearFiltersTarget?.classList.add('hidden')
    }
  }

  clearFilters() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.hasFilterContactTypeTarget) this.filterContactTypeTarget.value = 'all'
    if (this.hasFilterCountryTarget) this.filterCountryTarget.value = 'all'
    if (this.hasFilterCityTarget) this.filterCityTarget.value = 'all'

    this.filterContacts()
  }

  updateMapMarkers(contacts) {
    if (!this.map || !this.map.getSource('contacts')) return

    const contactsWithCoords = contacts.filter(c => c.latitude && c.longitude)

    const geojson = {
      type: 'FeatureCollection',
      features: contactsWithCoords.map(contact => ({
        type: 'Feature',
        id: parseInt(contact.id),
        geometry: {
          type: 'Point',
          coordinates: [contact.longitude, contact.latitude]
        },
        properties: {
          id: contact.id,
          name: contact.name,
          organization: contact.organization,
          city: contact.city,
          country: contact.country,
          email: contact.email,
          contact_type_abbr: contact.contact_type_abbr,
          show_url: contact.show_url,
          edit_url: contact.edit_url
        }
      }))
    }

    this.map.getSource('contacts').setData(geojson)
  }

  updateSidebar(contacts) {
    // Update sidebar contact items visibility
    document.querySelectorAll('.contact-item[data-contact-id]').forEach(item => {
      const contactId = parseInt(item.dataset.contactId)
      const isVisible = contacts.some(c => c.id === contactId)

      if (isVisible) {
        item.classList.remove('hidden')
      } else {
        item.classList.add('hidden')
      }
    })
  }

  // Stimulus action methods for map interactions
  selectContact(event) {
    const contactId = event.currentTarget.closest('.contact-item').dataset.contactId
    this.centerMapOnContact(contactId)
  }

  highlightContact(event) {
    const contactId = event.currentTarget.closest('.contact-item').dataset.contactId
    this.highlightMapMarker(contactId)
  }

  unhighlightContact(event) {
    const contactId = event.currentTarget.closest('.contact-item').dataset.contactId
    this.unhighlightMapMarker(contactId)
  }

  showContactPopup(contact) {
    const popupContent = `
      <div class="min-w-[280px]">
        <!-- Header with contact name -->
        <div class="px-4 pt-4 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-1">${contact.name}</h3>
          <div class="flex items-center gap-1.5 text-sm text-gray-600">
            <svg class="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
            </svg>
            <span>${contact.city}, ${contact.country}</span>
          </div>
          ${contact.contact_type ? `
            <div class="mt-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary bg-opacity-10 text-primary">
                ${contact.contact_type}
              </span>
            </div>
          ` : ''}
        </div>

        <!-- Info section -->
        ${contact.email ? `
          <div class="px-4 py-3 border-b border-gray-100">
            <div class="flex items-center gap-2 text-sm text-gray-600">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span class="truncate">${contact.email}</span>
            </div>
          </div>
        ` : ''}

        <!-- Actions -->
        <div class="px-4 py-3 bg-gray-50">
          <a href="${contact.edit_url}"
             data-turbo-frame="modal"
             class="flex items-center justify-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors shadow-sm">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            <span>Modifier le contact</span>
          </a>
        </div>
      </div>
    `

    // Create popup with custom styling
    new mapboxgl.Popup({
      maxWidth: '320px',
      className: 'contact-popup'
    })
      .setLngLat([contact.longitude, contact.latitude])
      .setHTML(popupContent)
      .addTo(this.map)
  }

  showMapError(message) {
    if (this.hasMapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-gray-100">
          <div class="text-center p-8">
            <i class="fas fa-map-marked-alt text-6xl text-gray-400 mb-4"></i>
            <p class="text-gray-600 text-lg">${message}</p>
            <p class="text-gray-500 text-sm mt-2">Vérifiez la configuration Mapbox</p>
          </div>
        </div>
      `
    }
  }

  get authToken() {
    // Get token from meta tag (same as other map controllers)
    const meta = document.head.querySelector('meta[name="mapbox_token"]')
    if (!meta) {
      console.error("Token Mapbox non trouvé dans les meta tags")
      return null
    }
    return meta.content
  }

  toggleSidebar() {
    this.sidebarExpanded = !this.sidebarExpanded

    if (this.sidebarExpanded) {
      // Expand sidebar
      this.sidebarContentTarget.parentElement.classList.remove('w-0')
      this.sidebarContentTarget.parentElement.classList.add('w-96')
      this.sidebarContentTarget.classList.remove('hidden')
      this.sidebarToggleIconTarget.classList.remove('fa-chevron-right')
      this.sidebarToggleIconTarget.classList.add('fa-chevron-left')
    } else {
      // Collapse sidebar
      this.sidebarContentTarget.parentElement.classList.remove('w-96')
      this.sidebarContentTarget.parentElement.classList.add('w-0')
      this.sidebarContentTarget.classList.add('hidden')
      this.sidebarToggleIconTarget.classList.remove('fa-chevron-left')
      this.sidebarToggleIconTarget.classList.add('fa-chevron-right')
    }

    // Resize map after sidebar toggle
    if (this.map) {
      setTimeout(() => {
        this.map.resize()
      }, 300)
    }
  }

  // Add any missing Stimulus actions from the view
  scrollToContact(contactId) {
    const sidebarItem = document.querySelector(`#sidebar_contact_${contactId}`)
    if (sidebarItem) {
      sidebarItem.scrollIntoView({ behavior: 'smooth', block: 'center' })
      sidebarItem.classList.add('bg-yellow-50')
      setTimeout(() => {
        sidebarItem.classList.remove('bg-yellow-50')
      }, 2000)
    }
  }

  applyJitterToOverlappingContacts(contacts) {
    // Group contacts by exact coordinates (rounded to avoid floating point issues)
    const coordsMap = new Map()

    contacts.forEach(contact => {
      const key = `${contact.latitude.toFixed(4)},${contact.longitude.toFixed(4)}`
      if (!coordsMap.has(key)) {
        coordsMap.set(key, [])
      }
      coordsMap.get(key).push(contact)
    })

    // Apply jitter to contacts at same location
    const jitteredContacts = []

    coordsMap.forEach((contactsAtLocation, coordsKey) => {
      if (contactsAtLocation.length === 1) {
        // Single contact at this location - no jitter needed
        jitteredContacts.push(contactsAtLocation[0])
      } else {
        // Multiple contacts at same location - apply jitter in a circle
        contactsAtLocation.forEach((contact, index) => {
          const jitteredContact = { ...contact }

          // Jitter amount: ~220 meters (0.002 degrees)
          const jitterAmount = 0.002

          // Distribute contacts in a circle
          const angle = (2 * Math.PI * index) / contactsAtLocation.length

          // Add some randomness to the radius (70-100% of jitterAmount)
          const radius = jitterAmount * (0.7 + Math.random() * 0.3)

          // Apply jitter using circular distribution
          jitteredContact.latitude = contact.latitude + (radius * Math.sin(angle))
          jitteredContact.longitude = contact.longitude + (radius * Math.cos(angle))

          jitteredContacts.push(jitteredContact)
        })
      }
    })

    return jitteredContacts
  }
}